import { clsx } from '@alexis/helpers/clsx';
import { uniq } from 'lodash-es';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { translateNumber } from '@wego/alexis/helpers/translation';

import Segment from '@components/flights/TripDetail/Segment';
import styles from '@components/flights/TripDetail/TripLegSummary.module.scss';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { getOperatingAirlinesInformation } from '@helpers/flight';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';

import FlightIcon from '@icons/flight.svg';
import HourglassIcon from '@icons/hourglass_half.svg';
import InfoIcon from '@icons/info.svg';
import MoonIcon from '@icons/moon_outlined.svg';

import { getIsRtlState, getLocaleState, getTranslationsState } from '@redux/selectors';

import type { SpecialFlightMessage } from '@wegoTypes/flights/metasearch/flightFareResultTrip';
import type { FlightFareResultTripLeg } from '@wegoTypes/flights/metasearch/flightFareResultTripLeg';
import type { Translations } from '@wegoTypes/translations';

interface TripLegSummaryProps {
  cardTitle: string;
  flightAmenities: FlightAmenities | undefined;
  isActiveLeg: boolean;
  isLegExpanded: boolean;
  itemWidth: number;
  leg: FlightFareResultTripLeg;
  legIndex: number;
  specialFlightMessages: Array<SpecialFlightMessage>;
  onExpandCollapseLegDetails(isLegDetailExpanded: boolean, legIndex: number): void;
  setRef(index: number, ref: HTMLDivElement | null): void;
}

const TripLegSummary: React.FC<TripLegSummaryProps> = ({
  cardTitle,
  flightAmenities,
  isActiveLeg,
  isLegExpanded,
  itemWidth,
  leg,
  legIndex,
  specialFlightMessages,
  onExpandCollapseLegDetails,
  setRef,
}) => {
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const displayViaText = (stopovers: string[], translations: Translations) => {
    return stopovers && stopovers.length ? ' ' + translations.via + ' ' + stopovers.join(', ') : '';
  };

  const displaySpecialFlightBanner = (leg: FlightFareResultTripLeg) => {
    // TODO remove split('-') once the BE changes the response
    const segmentDesignatorCodes = leg.segments.map(
      (segment) => segment.designatorCode.split('-')[0],
    );
    const specialFlightMessage = specialFlightMessages.find((specialFlightMessage) =>
      segmentDesignatorCodes.includes(specialFlightMessage.segmentId),
    );

    return specialFlightMessage ? (
      <div className={styles.specialFlightMessages}>
        <div className={styles.specialFlightMessage}>
          <InfoIcon className={styles.icon} />
          <span>{specialFlightMessage.header}</span>
        </div>

        <div className={clsx(styles.tooltip, styles.bottom)}>
          <div>{specialFlightMessage.title}</div>
          <div dangerouslySetInnerHTML={{ __html: specialFlightMessage.messages }} />
        </div>
      </div>
    ) : null;
  };

  const segmentAirlineLength = useMemo(() => {
    return uniq(leg.segments.map((segment) => segment.airlineName)).length;
  }, [leg.segments]);

  return (
    <div
      data-pw='tripDetails_itineraryCard'
      className={clsx(styles.itineraryCard, isActiveLeg && styles.active, isRtl && styles.rtl)}
      style={{ width: `${itemWidth}px` }}
    >
      {specialFlightMessages.length > 0 ? displaySpecialFlightBanner(leg) : null}
      <div className={styles.itineraryCardTitle}>{cardTitle}</div>

      <div className={styles.flightDetails}>
        <div data-pw='itineraryCard_legDepartureInfo' className={styles.departureColumn}>
          <div className={styles.date}>{leg.departureDate}</div>

          <div className={styles.airportCode}>
            <span data-testid='leg-departure-airport-code'>{leg.departureAirportCode}</span>

            <span data-testid='leg-departure-time'>{leg.segments[0].departureTime}</span>
          </div>

          <div className={styles.airportName}>{leg.segments[0].departureAirportName}</div>
        </div>

        <FlightIcon className={styles.icon} />
        <div data-pw='itineraryCard_legArrivalInfo' className={styles.arrivalColumn}>
          <div className={clsx(styles.date, leg.durationDays !== 0 && styles.oneOrMoreNights)}>
            {leg.arrivalDate}
          </div>

          <div className={styles.airportCode}>
            <span data-testid='leg-arrival-airport-code'>{leg.arrivalAirportCode}</span>

            <span className={styles.arrivalTimeContainer}>
              <span data-testid='leg-arrival-time'>
                {leg.segments[leg.segments.length - 1].arrivalTime}
              </span>

              {leg.durationDays ? (
                <div className={styles.nightCount} data-testid='leg-overnight'>
                  {`(${leg.durationDays > 0 ? '+' : ''}${translateNumber(
                    leg.durationDays,
                    locale === 'fa',
                  )})`}
                </div>
              ) : null}
            </span>
          </div>

          <div className={styles.airportName}>
            {leg.segments[leg.segments.length - 1].arrivalAirportName}
          </div>
        </div>
      </div>

      <div className={styles.flightDurationContainer}>
        <div data-pw='itineraryCard_legDurationInfo' className={styles.flightDurationLeftColumn}>
          <div className={styles.duration}>
            <span data-testid='duration'>{leg.duration}</span>
            <span>{displayViaText(leg.stopoverAirportCodes, translations)}</span>
          </div>

          {leg.longStopover ? (
            <div className={styles.longStopover}>
              <HourglassIcon className={styles.icon} />
              {translations.long_stopover} - {leg.stopoverDuration}
            </div>
          ) : null}

          {leg.overnight ? (
            <div className={styles.OvernightFlight}>
              <MoonIcon className={styles.icon} />

              {translations.overnight_flight}
            </div>
          ) : null}
        </div>
        <div className={styles.airlineInfo}>
          <div className={styles.airlineImage}>
            <img
              data-testid='leg-airline-image'
              alt={leg.airlineCodes[0]}
              className={styles.image}
              src={cloudinaryImageTransformations(
                `https://res.cloudinary.com/wego/image/upload/c_fit,w_200,h_70/${CLOUDINARY_DAILY_KEY}/flights/airlines_rectangular/${leg.airlineCodes[0]}.png`,
                'c_fit',
                'w_100',
                'h_100',
                'q_auto',
                'f_auto',
                'fl_lossy',
              )}
            />

            {segmentAirlineLength > 1 && (
              <div className={styles.additionalNoOfAirlines}>
                +{translateNumber(segmentAirlineLength - 1, locale === 'fa')}
              </div>
            )}
          </div>

          <span className={styles.operatingAirline}>
            {getOperatingAirlinesInformation(leg, translations)}
          </span>
        </div>
      </div>

      {/* Segment details */}
      <div
        ref={(el) => setRef(legIndex, el)}
        className={clsx(styles.segmentsBreakdown, !isLegExpanded && styles.hidden)}
      >
        {/* Fade away details when collapsed */}
        {!isLegExpanded ? <div className={styles.overlay}></div> : null}

        {leg.segments.map((segment, index) => (
          <Segment
            key={index}
            flightAmenities={flightAmenities}
            leg={leg}
            legIndex={legIndex}
            segment={segment}
            segmentIndex={index}
          />
        ))}
      </div>

      <div
        className={styles.showHideDetails}
        data-testid='show-hide-details'
        onClick={() => onExpandCollapseLegDetails(!isLegExpanded, legIndex)}
      >
        <div className={styles.showHideDetailsText}>
          {isLegExpanded ? translations.hide_details : translations.show_details}
          <div className={clsx(styles.chevron, isLegExpanded ? styles.top : styles.bottom)}></div>
        </div>
      </div>
    </div>
  );
};

export default TripLegSummary;
